import { computed, getCurrentScope, onScopeDispose, readonly, ref, type Ref } from 'vue';

/**
 * One debounced call, and only its latest response is kept.
 *
 * `run` waits `delay` ms (default 300, trailing, same rule as the kit's
 * `debounce`: `delay <= 0` runs now). A newer `run` aborts the controller of
 * the call that is in flight and drops that call's response, including an
 * `AbortError`. `cancel` and effect-scope disposal do the same and also drop
 * a call that is still waiting out the delay.
 */
export interface LatestRequest<A extends unknown[], R> {
  /** Schedule `fn` with these arguments. A waiting call is replaced. */
  run(...args: A): void;
  /** Run the waiting call now. No-op when none is waiting. */
  flush(): void;
  /** Abort the in-flight call and drop the waiting one. */
  cancel(): void;
  readonly result: Readonly<Ref<R | undefined>>;
  readonly error: Readonly<Ref<unknown>>;
  /** True while a call is waiting out the delay or in flight. */
  readonly pending: Readonly<Ref<boolean>>;
}

export function useLatestRequest<A extends unknown[], R>(
  fn: (signal: AbortSignal, ...args: A) => Promise<R>,
  options?: { delay?: number },
): LatestRequest<A, R> {
  const delay = options?.delay ?? 300;
  const result = ref<R | undefined>(undefined) as Ref<R | undefined>;
  const error = ref<unknown>(undefined);
  const debouncing = ref(false);
  const inFlight = ref(false);
  const pending = computed(() => debouncing.value || inFlight.value);

  let generation = 0;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingArgs: A | null = null;

  /** The in-flight call is no longer the latest: abort it and ignore its reply. */
  const retire = () => {
    generation += 1;
    controller?.abort();
    controller = null;
    inFlight.value = false;
  };

  const settle = (gen: number, signal: AbortSignal, value?: R, err?: unknown) => {
    if (gen !== generation) return;
    inFlight.value = false;
    if (err !== undefined) {
      // The signal we handed out was aborted, or the rejection is an AbortError.
      // Either way it is cancellation, not a failure of the request.
      if (signal.aborted || isAbortError(err)) return;
      error.value = err;
      return;
    }
    result.value = value;
    error.value = undefined;
  };

  const start = (args: A) => {
    debouncing.value = false;
    const gen = generation + 1;
    generation = gen;
    const ac = new AbortController();
    controller = ac;
    inFlight.value = true;
    fn(ac.signal, ...args).then(
      (value) => settle(gen, ac.signal, value),
      (err: unknown) => settle(gen, ac.signal, undefined, err),
    );
  };

  const fire = () => {
    timer = null;
    if (pendingArgs === null) return;
    const args = pendingArgs;
    pendingArgs = null;
    start(args);
  };

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const run = (...args: A) => {
    pendingArgs = args;
    debouncing.value = true;
    retire();
    if (delay <= 0) {
      clearTimer();
      fire();
      return;
    }
    clearTimer();
    timer = setTimeout(fire, delay);
  };

  const flush = () => {
    clearTimer();
    fire();
  };

  const cancel = () => {
    clearTimer();
    pendingArgs = null;
    debouncing.value = false;
    retire();
  };

  if (getCurrentScope()) onScopeDispose(cancel);

  return {
    run,
    flush,
    cancel,
    // Vue's readonly() deep-freezes the value; the contract only freezes the ref.
    result: readonly(result) as Readonly<Ref<R | undefined>>,
    error: readonly(error),
    pending: readonly(pending),
  };
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}
